import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Camera,
  Video,
  HeartPulse,
  Scale,
  Syringe,
  Sparkles,
  AlertTriangle,
  Home,
  ChevronLeft,
  PlusCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePets } from '@/hooks/usePets';

interface CreateActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

type ActionGroup = 'content' | 'health' | 'community';

interface ActionDef {
  id: string;
  label: string;
  icon: typeof Camera;
  group: ActionGroup;
  tone: string;
  /** Mark "alert/highlight" actions (Lost, Adoption, Milestone) */
  highlight?: boolean;
  /** Build the destination route given a pet id */
  buildRoute: (petId: string) => string;
  /** Optional nav state passed to react-router (used by CreatePost) */
  buildState?: (petId: string) => Record<string, any>;
}

const CreateActionSheet = ({ isOpen, onClose }: CreateActionSheetProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pets: petsRaw, isLoading: petsLoading } = usePets();
  const pets: any[] = (petsRaw as any[]) || [];

  const [pendingAction, setPendingAction] = useState<ActionDef | null>(null);

  useEffect(() => {
    if (!isOpen) setPendingAction(null);
  }, [isOpen]);

  const actions: ActionDef[] = [
    {
      id: 'photo',
      label: t('create.photoMemory', 'Photo Memory'),
      icon: Camera,
      group: 'content',
      tone: 'from-pink-500 to-rose-500',
      buildRoute: () => '/create-post',
      buildState: (petId) => ({ selectedPetId: petId, mediaType: 'photo' }),
    },
    {
      id: 'video',
      label: t('create.videoPost', 'Video Post'),
      icon: Video,
      group: 'content',
      tone: 'from-fuchsia-500 to-purple-500',
      buildRoute: () => '/create-post',
      buildState: (petId) => ({ selectedPetId: petId, mediaType: 'video' }),
    },
    {
      id: 'health',
      label: t('create.healthRecord', 'Health Record'),
      icon: HeartPulse,
      group: 'health',
      tone: 'from-emerald-500 to-teal-500',
      buildRoute: (petId) => `/medical/${petId}/records/new`,
    },
    {
      id: 'weight',
      label: t('create.weightLog', 'Weight Log'),
      icon: Scale,
      group: 'health',
      tone: 'from-sky-500 to-blue-500',
      buildRoute: (petId) => `/medical/${petId}?tab=weight`,
    },
    {
      id: 'vaccination',
      label: t('create.vaccination', 'Vaccination'),
      icon: Syringe,
      group: 'health',
      tone: 'from-indigo-500 to-violet-500',
      buildRoute: (petId) => `/medical/${petId}/vaccinations/new`,
    },
    {
      id: 'milestone',
      label: t('create.milestone', 'Pet Milestone'),
      icon: Sparkles,
      group: 'health',
      tone: 'from-amber-500 to-orange-500',
      highlight: true,
      buildRoute: () => '/create-post',
      buildState: (petId) => ({ selectedPetId: petId, postFlag: 'milestone' }),
    },
    {
      id: 'lost',
      label: t('create.lostAlert', 'Lost Pet Alert'),
      icon: AlertTriangle,
      group: 'community',
      tone: 'from-red-500 to-orange-600',
      highlight: true,
      buildRoute: () => '/create-post',
      buildState: (petId) => ({ selectedPetId: petId, postFlag: 'lost' }),
    },
    {
      id: 'adoption',
      label: t('create.adoptionPost', 'Adoption Post'),
      icon: Home,
      group: 'community',
      tone: 'from-teal-500 to-cyan-600',
      highlight: true,
      buildRoute: () => '/create-post',
      buildState: (petId) => ({ selectedPetId: petId, postFlag: 'adoption' }),
    },
  ];

  const groups: { id: ActionGroup; title: string }[] = [
    { id: 'content', title: t('create.groupContent', 'Content') },
    { id: 'health', title: t('create.groupHealth', 'Health') },
    { id: 'community', title: t('create.groupCommunity', 'Community') },
  ];

  const closeAndNavigate = (to: string, state?: Record<string, any>) => {
    onClose();
    setTimeout(() => navigate(to, state ? { state } : undefined), 60);
  };

  const handleActionPick = (a: ActionDef) => {
    if (petsLoading) return;

    if (pets.length === 0) {
      // No pets yet — send the user to create one
      closeAndNavigate('/my-pets');
      return;
    }
    if (pets.length === 1) {
      const onlyPet = pets[0];
      closeAndNavigate(a.buildRoute(onlyPet.id), a.buildState?.(onlyPet.id));
      return;
    }
    setPendingAction(a);
  };

  const handlePetPick = (petId: string) => {
    if (!pendingAction) return;
    const a = pendingAction;
    closeAndNavigate(a.buildRoute(petId), a.buildState?.(petId));
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        className="p-0 rounded-t-3xl max-h-[85dvh] overflow-y-auto"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <SheetHeader className="px-5 pt-5 pb-2">
          <div className="flex items-center justify-center relative">
            {pendingAction && (
              <button
                onClick={() => setPendingAction(null)}
                className="absolute left-0 p-1 rounded-full hover:bg-muted"
                aria-label={t('common.back', 'Back')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <SheetTitle className="text-center text-lg font-bold">
              {pendingAction
                ? t('create.chooseAPet', 'Choose a pet')
                : t('create.title', 'Create')}
            </SheetTitle>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            {pendingAction
              ? t('create.chooseAPetSubtitle', 'Select which pet this action applies to')
              : t('create.subtitle', 'Share, log or report — choose an action')}
          </p>
        </SheetHeader>

        {!pendingAction && (
          <div className="px-5 pb-6 space-y-5">
            {petsLoading && (
              <p className="px-1 text-xs text-muted-foreground">
                {t('common.loading', 'Loading...')}
              </p>
            )}
            {groups.map((g) => {
              const items = actions.filter((a) => a.group === g.id);
              if (items.length === 0) return null;
              return (
                <section key={g.id}>
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2 px-1">
                    {g.title}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {items.map((a) => {
                      const Icon = a.icon;
                      return (
                        <button
                          key={a.id}
                          onClick={() => handleActionPick(a)}
                          disabled={petsLoading}
                          className={cn(
                            'group relative flex items-center gap-3 rounded-2xl p-3 border text-left',
                            'hover:shadow-md active:scale-[0.98] transition-all min-h-[64px]',
                            petsLoading && 'opacity-60 cursor-wait pointer-events-none',
                            a.highlight
                              ? 'bg-gradient-to-br from-amber-50 to-rose-50 dark:from-amber-950/30 dark:to-rose-950/30 border-amber-300 dark:border-amber-700 ring-1 ring-amber-200 dark:ring-amber-800 shadow-sm'
                              : 'bg-card border-border/60'
                          )}
                        >
                          {a.highlight && (
                            <span className="absolute -top-2 -right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500 text-white shadow">
                              {t('create.specialBadge', 'Special')}
                            </span>
                          )}
                          <span
                            className={cn(
                              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
                              a.tone
                            )}
                          >
                            <Icon className="h-5 w-5" />
                          </span>
                          <span className="text-sm font-semibold leading-tight">
                            {a.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {pendingAction && (
          <div className="px-5 pb-6 space-y-2">
            {pets.length === 0 ? (
              <div className="py-6 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('create.noPetsYet', "You don't have any pets yet.")}
                </p>
                <Button onClick={() => closeAndNavigate('/my-pets')}>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  {t('create.addPet', 'Add a pet')}
                </Button>
              </div>
            ) : (
              pets.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handlePetPick(p.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-card hover:shadow-md active:scale-[0.99] transition-all"
                >
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">
                    {p.photo_url || p.image_url ? (
                      <img
                        src={p.photo_url || p.image_url}
                        alt={p.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-base">🐾</span>
                    )}
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    {p.species && (
                      <p className="text-xs text-muted-foreground truncate">
                        {p.species}
                        {p.breed ? ` · ${p.breed}` : ''}
                      </p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CreateActionSheet;
